import React, { useEffect, useState } from 'react';
import UiContent from '../../../Components/Common/UiContent';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import {
	Card,
	CardBody,
	CardHeader,
	Col,
	Container,
	Input,
	Label,
	Row,
	Table,
	Tooltip,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { Link } from 'react-router-dom';
import {
	getEstablishmentList,
	getModulesList,
	getModuleEstablishmentList,
	addNewModuleEstablishment,
	updateModuleEstablishment,
	getModuleEstablishment,
	deleteModule,
} from '../../../slices/thunks';
import AddEditModuleModal from './Add_Edit/AddEditModuleModal';

import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Modules = () => {
	const breadcrumbLinks = [{ to: '#', label: 'Inicio', icon: 'icon-house' }];
	const dispatch = useDispatch();

	const [checkboxStates, setCheckboxStates] = useState({});

	// Estado para el modal de módulos
	const [modal, setModal] = useState(false);
	const [isEdit, setIsEdit] = useState(false);
	const [dataModule, setDataModule] = useState({});

	// Estado para tooltips de eliminar
	const [tooltipOpen, setTooltipOpen] = useState({});

	function handleToggleModal() {
		setModal(!modal);
		setDataModule({});
		setIsEdit(false);
	}

	const handleClickEdit = module => {
		setDataModule(module);
		setIsEdit(true);
		setModal(true);
	};

	const handleClickDelete = module => {
		if (module.code === 'BSE') {
			toast.error('El módulo base (BSE) no puede ser eliminado.', { autoClose: 3000 });
			return;
		}
		if (window.confirm(`¿Estás seguro de que deseas eliminar el módulo "${module.name}"? Esta acción no se puede deshacer.`)) {
			dispatch(deleteModule(module));
		}
	};

	// Verificar si un módulo tiene establecimientos activos
	const hasActiveEstablishments = moduleId => {
		return establishments.some(
			establishment =>
				checkboxStates[`${establishment._id}_${moduleId}`] === 'Activo'
		);
	};

	// Verificar si se puede eliminar un módulo
	const canDeleteModule = module => {
		// No se puede eliminar el módulo base
		if (module.code === 'BSE') return false;
		// No se puede eliminar si tiene establecimientos activos
		if (hasActiveEstablishments(module._id)) return false;
		return true;
	};

	// Obtener mensaje de tooltip para el botón eliminar
	const getDeleteTooltipText = module => {
		if (module.code === 'BSE') {
			return 'El módulo base no puede ser eliminado';
		}
		if (hasActiveEstablishments(module._id)) {
			return 'Desactive el módulo en todos los establecimientos primero';
		}
		return 'Eliminar módulo';
	};

	const toggleTooltip = moduleId => {
		setTooltipOpen(prev => ({
			...prev,
			[moduleId]: !prev[moduleId],
		}));
	};

	const selectorSettings = createSelector(
		state => state.Settings,
		result => ({
			modules: result.getModulesList,
			isLoadingSettings: result.isLoadingList,
		})
	);

	const { modules, isLoadingSettings } = useSelector(selectorSettings);

	const selectorEstablishment = createSelector(
		state => state.Establishments,
		result => ({
			establishments: result.itemEstablishmentList,
			isLoadingEstablishments: result.isLoadingList,
		})
	);

	const { establishments, isLoadingEstablishments } = useSelector(
		selectorEstablishment
	);

	useEffect(() => {
		dispatch(getModulesList());
		dispatch(getEstablishmentList());
	}, [dispatch]);

	useEffect(() => {
		const initializeCheckboxStates = async () => {
			try {
				// Obtener todos los registros de relaciones entre módulos y establecimientos
				const response = await dispatch(
					getModuleEstablishmentList({ limit: 9999 })
				).unwrap();
				const initialStates = {};

				// Recorrer la lista para establecer los estados iniciales de los checkboxes
				response.forEach(({ module_id, establishment_id, active }) => {
					// Solo habilita si 'active' es true
					initialStates[`${establishment_id}_${module_id}`] = active
						? 'Activo'
						: 'Desactivo';
				});

				setCheckboxStates(initialStates);
			} catch (error) {
				console.error('Error fetching module establishment list:', error);
			}
		};

		if (establishments.length > 0 && modules.length > 0) {
			initializeCheckboxStates();
		}
	}, [establishments, modules, dispatch]);

	const isColumnActive = moduleId => {
		return establishments.every(
			establishment =>
				checkboxStates[`${establishment._id}_${moduleId}`] === 'Activo'
		);
	};

	const handleCheckboxChange = async (
		establishmentId,
		moduleId,
		isChecked
	) => {
		try {
			// Intentar obtener la relación existente
			let response = null;
			try {
				response = await dispatch(
					getModuleEstablishment({
						establishment_id: establishmentId,
						module_id: moduleId,
					})
				).unwrap();
			} catch (error) {
				// Si es 404, la relación no existe - es normal para nuevos módulos
				if (error?.response?.status === 404 || 
				    error?.message?.includes('not found') ||
				    error?.message?.includes('Document not found')) {
					response = null;
				} else {
					// Otros errores deben propagarse
					throw error;
				}
			}

			const module_establishment = {
				module_id: moduleId,
				establishment_id: establishmentId,
				active: isChecked,
			};

			setCheckboxStates(prevState => ({
				...prevState,
				[`${establishmentId}_${moduleId}`]: isChecked
					? 'Activo'
					: 'Desactivo',
			}));

			// Si existe la relación, actualizar. Si no, crear nueva (solo si se está activando)
			if (response && Object.keys(response).length > 0 && response._id) {
				module_establishment._id = response._id;
				module_establishment.active = isChecked;
				await dispatch(
					updateModuleEstablishment(module_establishment)
				).unwrap();
			} else if (isChecked) {
				await dispatch(
					addNewModuleEstablishment(module_establishment)
				).unwrap();
			}

			toast.success(
				'El Módulo del establecimiento se ha actualizado exitosamente',
				{ autoClose: 3000 }
			);
		} catch (error) {
			console.error('Error handling checkbox change:', error);
		}
	};

	const handleColumnToggle = async (moduleId, isActive) => {
		const updatedStates = { ...checkboxStates };
		let updatedCount = 0;

		// Filtrar solo los establecimientos que necesitan actualización
		const establishmentsToUpdate = establishments.filter(establishment => {
			const key = `${establishment._id}_${moduleId}`;
			return isActive
				? checkboxStates[key] !== 'Activo' // Solo activa si no está activo
				: checkboxStates[key] !== 'Desactivo'; // Solo desactiva si no está desactivado
		});

		const total = establishmentsToUpdate.length;

		if (total === 0) {
			toast.info('No hay cambios necesarios para esta columna.', {
				autoClose: 3000,
			});
			return;
		}

		// Mostrar mensaje inicial
		const toastId = toast.info(`Actualizando 0 de ${total} módulos...`, {
			autoClose: false,
		});

		try {
			for (const establishment of establishmentsToUpdate) {
				const key = `${establishment._id}_${moduleId}`;
				updatedStates[key] = isActive ? 'Activo' : 'Desactivo';

				// Intentar obtener la relación existente
				let response = null;
				try {
					response = await dispatch(
						getModuleEstablishment({
							establishment_id: establishment._id,
							module_id: moduleId,
						})
					).unwrap();
				} catch (error) {
					// Si es 404, la relación no existe - es normal para nuevos módulos
					if (error?.response?.status === 404 || 
					    error?.message?.includes('not found') ||
					    error?.message?.includes('Document not found')) {
						response = null;
					} else {
						// Otros errores deben propagarse
						throw error;
					}
				}

				const module_establishment = {
					module_id: moduleId,
					establishment_id: establishment._id,
					active: isActive,
				};

				if (response && Object.keys(response).length > 0 && response._id) {
					module_establishment._id = response._id;
					await dispatch(
						updateModuleEstablishment(module_establishment)
					).unwrap();
				} else if (isActive) {
					await dispatch(
						addNewModuleEstablishment(module_establishment)
					).unwrap();
				}

				// Actualizar el progreso
				updatedCount++;
				toast.update(toastId, {
					render: `Actualizando ${updatedCount} de ${total} módulos...`,
					type: toast.TYPE.INFO,
					autoClose: false,
				});
			}

			// Actualizar el estado local y mostrar éxito
			setCheckboxStates(updatedStates);
			toast.update(toastId, {
				render: `Se han actualizado ${updatedCount} de ${total} módulos exitosamente`,
				type: toast.TYPE.SUCCESS,
				autoClose: 3000,
			});
		} catch (error) {
			console.log(error);
			console.error('Error handling column toggle:', error);
			toast.update(toastId, {
				render: 'Ocurrió un error al actualizar los módulos.',
				type: toast.TYPE.ERROR,
				autoClose: 3000,
			});
		}
	};

	return (
		<React.Fragment>
			<style>{`
				.modules-table-container {
					position: relative;
				}
				.modules-table-container .table-responsive {
					max-height: calc(100vh - 300px);
					overflow-x: auto;
					overflow-y: auto;
				}
				.modules-table-container .table-responsive::-webkit-scrollbar {
					width: 8px;
					height: 8px;
				}
				.modules-table-container .table-responsive::-webkit-scrollbar-track {
					background: #f1f1f1;
					border-radius: 4px;
				}
				.modules-table-container .table-responsive::-webkit-scrollbar-thumb {
					background: #888;
					border-radius: 4px;
				}
				.modules-table-container .table-responsive::-webkit-scrollbar-thumb:hover {
					background: #555;
				}
				.modules-table th,
				.modules-table td {
					min-width: 220px;
					max-width: 250px;
					vertical-align: top;
				}
				.modules-table th:first-child,
				.modules-table th:nth-child(2) {
					vertical-align: bottom;
				}
				.module-header-content {
					position: relative;
					min-height: 200px;
				}
				.module-header-bottom {
					position: absolute;
					bottom: 0;
					left: 0;
					right: 0;
				}
				.modules-table th:first-child,
				.modules-table td:first-child {
					min-width: 200px;
					position: sticky;
					left: 0;
					background: white;
					z-index: 1;
					box-shadow: 2px 0 5px rgba(0,0,0,0.1);
				}
				.modules-table th:nth-child(2),
				.modules-table td:nth-child(2) {
					min-width: 100px;
					position: sticky;
					left: 200px;
					background: white;
					z-index: 1;
					box-shadow: 2px 0 5px rgba(0,0,0,0.1);
				}
				.modules-table thead th {
					position: sticky;
					top: 0;
					background: #f8f9fa;
					z-index: 2;
				}
				.modules-table thead th:first-child,
				.modules-table thead th:nth-child(2) {
					z-index: 3;
				}
				.module-description {
					font-size: 0.7rem;
					line-height: 1.35;
					word-wrap: break-word;
					white-space: normal;
					max-height: 9.5em;
					overflow: hidden;
					position: relative;
					text-align: justify;
				}
				.module-description::after {
					content: '...';
					position: absolute;
					bottom: 0;
					right: 0;
					background: linear-gradient(to right, transparent, white 30%);
					padding-left: 15px;
					display: none;
				}
				.module-description.truncated::after {
					display: inline;
				}
			`}</style>
			<UiContent />
			<div className='page-content'>
				<Container fluid>
					<BreadCrumb title='Modulos' links={breadcrumbLinks} icon={'icon-layout-grid'} />

					<Row>
						<Col lg={12}>
							<Card>
								<CardHeader className='border-0 pb-0'>
									<div className='d-flex align-items-center justify-content-between mb-2'>
										<div>
											<h5 className='card-title mb-0'>Gestión de Módulos</h5>
											<small className='text-muted'>
												{modules.length} módulo{modules.length !== 1 ? 's' : ''} configurado{modules.length !== 1 ? 's' : ''}
												{modules.length > 4 && (
													<span className='ms-2 text-info'>
														<i className='ri-arrow-left-right-line'></i> Scroll horizontal disponible
													</span>
												)}
											</small>
										</div>
										<button
											className='btn btn-label btn-primary'
											onClick={handleToggleModal}
										>
											<i className='ri-add-line label-icon'></i>{' '}
											Agregar Módulo
										</button>
									</div>
								</CardHeader>
								<CardBody>
									<Row>
										<Col xl={12}>
																						<div className='table-responsive table-card modules-table-container'>
																							<Table className='table-hover table-striped align-middle table-nowrap mb-0 modules-table'>
													<thead>
														<tr>
															<th scope='col'>Centro</th>
															<th scope='col'>Estado</th>
															{modules.map(module => (
																<th
																	key={module._id}
																	scope='col'
																	className='text-center'
																>
																								<div className='module-header-content'>
																									<div className='module-header-top'>
																										<div className='d-flex align-items-center justify-content-between mb-2'>
																											<span 
																												className='fw-semibold text-truncate'
																												style={{ maxWidth: '120px' }}
																												title={module.name}
																											>
																												{module.name}
																											</span>
																											<div className='d-flex gap-2 flex-shrink-0'>
																												<Link
																													to='#'
																													onClick={() => handleClickEdit(module)}
																													className='text-primary'
																													title='Editar módulo'
																												>
																													<i className='ri-pencil-fill align-bottom'></i>
																												</Link>
																												<span id={`delete-tooltip-${module._id}`}>
																													<Link
																														to='#'
																														onClick={e => {
																															if (!canDeleteModule(module)) {
																																e.preventDefault();
																																return;
																															}
																															handleClickDelete(module);
																														}}
																														className={canDeleteModule(module) ? 'text-danger' : 'text-muted'}
																														style={{
																															pointerEvents: canDeleteModule(module) ? 'auto' : 'none',
																															opacity: canDeleteModule(module) ? 1 : 0.5,
																															cursor: canDeleteModule(module) ? 'pointer' : 'not-allowed',
																														}}
																													>
																														<i className='ri-delete-bin-line align-bottom'></i>
																													</Link>
																													<Tooltip
																														placement='top'
																														isOpen={tooltipOpen[module._id] || false}
																														target={`delete-tooltip-${module._id}`}
																														toggle={() => toggleTooltip(module._id)}
																													>
																														{getDeleteTooltipText(module)}
																													</Tooltip>
																												</span>
																										</div>
																									</div>
																									<p 
																										className='text-muted mb-1 module-description'
																										title={module.description}
																									>
																										{module.description}
																									</p>
																								</div>
																								<div className='module-header-bottom'>
																									<h5 
																										className='text-muted mb-2'
																										style={{ fontSize: '0.85rem' }}
																									>
																										{module.price} UF
																									</h5>
																									<div className='form-check d-flex justify-content-center'>
																		<Input
																			className='form-check-input'
																			type='checkbox'
																			id={`SwitchColumn_${module._id}`}
																			onChange={() =>
																				handleColumnToggle(
																					module._id,
																					!isColumnActive(
																						module._id
																					)
																				)
																			}
																			checked={isColumnActive(
																				module._id
																			)}
																		/>
																		<Label
																			className='form-check-label ms-2'
																			htmlFor={`SwitchColumn_${module._id}`}
																		>
																																					{isColumnActive(
																																						module._id
																																					)
																																						? 'Desactivar todos'
																																						: 'Activar todos'}
																																					</Label>
																																				</div>
																																		</div>
																																</div>
																															</th>
															))}
														</tr>
												</thead>
													<tbody>
														{establishments.map(establishment => (
															<tr key={establishment._id}>
																<td className='fw-semibold'>
																	{establishment.name}
																</td>
																<td className='fw-semibold'>
																	<span
																		className='badge'
																		style={{
																			backgroundColor:
																				establishment
																					?.establishment_status
																					?.bgcolor,
																			color: establishment
																				?.establishment_status
																				?.txcolor,
																		}}
																	>
																		{establishment?.establishment_status?.name?.toUpperCase() ??
																			'-'}
																	</span>
																</td>
																{modules.map(module => (
																	<td key={module._id}>
																		<div className='form-check form-switch'>
																			<Input
																				className='form-check-input'
																				type='checkbox'
																				role='switch'
																				id={`SwitchCheck_${establishment._id}_${module._id}`}
																				checked={
																					checkboxStates[
																						`${establishment._id}_${module._id}`
																					] === 'Activo'
																				}
																				onChange={e =>
																					handleCheckboxChange(
																						establishment._id,
																						module._id,
																						e.target
																							.checked
																					)
																				}
																			/>
																			<Label
																				className='form-check-label ms-2'
																				htmlFor={`SwitchCheck_${establishment._id}_${module._id}`}
																			>
																				{checkboxStates[
																					`${establishment._id}_${module._id}`
																				] || 'Desactivo'}
																			</Label>
																		</div>
																	</td>
																))}
															</tr>
														))}
													</tbody>
												</Table>
											</div>
										</Col>
									</Row>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</Container>
			</div>

			<AddEditModuleModal
				isOpen={modal}
				isEdit={isEdit}
				dataModule={dataModule}
				handleToggleModal={handleToggleModal}
			/>
		</React.Fragment>
	);
};

export default Modules;

// modulo * precio * cantidadMod + base/establecimientos_activos

// junio desactivar EPP para todos los centros
